/**
 * EventCacheService - IndexedDB 事件緩存服務
 *
 * 提供事件的本地緩存功能，支援：
 * - 批量存儲事件
 * - 複雜查詢（支援 EventQueryBuilder 的 filters 格式）
 * - 排序和分頁
 * - 緩存元數據管理
 * - 過期清理
 *
 * 使用範例：
 *   import { eventCacheService } from '@shared/services/event';
 *
 *   // 存儲事件
 *   await eventCacheService.saveEvents(events);
 *
 *   // 查詢事件
 *   const results = await eventCacheService.queryEvents(
 *     { type: 'alarm', is_resolved: false },
 *     { orderBy: [{ field: 'created_at', ascending: false }], limit: 100 }
 *   );
 */

import type { EventRow, EventType, EventLevel, EventPriority, EventSource } from './types'

// ============ 常數 ============

const DB_NAME = 'om-events-cache'
const DB_VERSION = 1
const EVENTS_STORE = 'events'
const META_STORE = 'meta'

// ============ 類型定義 ============

/** 緩存事件（包含緩存時間戳） */
export interface CachedEvent extends EventRow {
  _cachedAt: number
}

/** 緩存元數據 */
export interface CacheMeta {
  key: string
  lastSyncTime: number | null
  oldestEventDate: number | null
  newestEventDate: number | null
  eventCount: number
}

/** 排序選項 */
export interface OrderByOption {
  field: string
  ascending: boolean
}

/** 查詢選項 */
export interface CacheQueryOptions {
  orderBy?: OrderByOption[]
  limit?: number
  offset?: number
}

/** 查詢篩選條件 */
export interface CacheQueryFilters {
  id?: string
  type?: EventType
  types?: EventType[]
  priority?: EventPriority
  source?: EventSource
  source_detail?: string
  event_level?: EventLevel
  organization_id?: string
  field_id?: string
  field_ids?: string[]
  device_id?: string
  startDate?: string | Date
  endDate?: string | Date
  createdAfter?: string | Date
  is_read?: boolean
  is_resolved?: boolean
  keyword?: string
  min_duration_minutes?: number
  includeDeleted?: boolean
}

// ============ 服務類別 ============

class EventCacheService {
  private db: IDBDatabase | null = null
  private isInitialized: boolean = false
  private initPromise: Promise<IDBDatabase> | null = null

  /**
   * 初始化 IndexedDB
   */
  async init(): Promise<IDBDatabase> {
    // 防止重複初始化
    if (this.isInitialized && this.db) {
      return this.db
    }

    // 如果正在初始化，等待完成
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('[EventService] IndexedDB 開啟失敗:', request.error)
        this.initPromise = null
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        this.isInitialized = true
        console.log('[EventService] IndexedDB 已連接')
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        console.log('[EventService] 建立/升級 IndexedDB schema...')

        // 建立 events store
        if (!db.objectStoreNames.contains(EVENTS_STORE)) {
          const eventsStore = db.createObjectStore(EVENTS_STORE, { keyPath: 'id' })

          // 建立索引以支援高效查詢
          eventsStore.createIndex('created_at', 'created_at', { unique: false })
          eventsStore.createIndex('updated_at', 'updated_at', { unique: false })
          eventsStore.createIndex('type', 'type', { unique: false })
          eventsStore.createIndex('priority', 'priority', { unique: false })
          eventsStore.createIndex('field_id', 'field_id', { unique: false })
          eventsStore.createIndex('is_resolved', 'is_resolved', { unique: false })
          eventsStore.createIndex('is_read', 'is_read', { unique: false })
          eventsStore.createIndex('_cachedAt', '_cachedAt', { unique: false })

          console.log('[EventService] events store 已建立')
        }

        // 建立 meta store
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'key' })
          console.log('[EventService] meta store 已建立')
        }
      }
    })

    return this.initPromise
  }

  /**
   * 確保 DB 已初始化
   */
  async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init()
    }
    return this.db!
  }

  // ==================== 事件存儲操作 ====================

  /**
   * 批量存儲事件（覆蓋相同 ID）
   * @param events - 事件陣列
   * @returns 存儲的事件數量
   */
  async saveEvents(events: EventRow[]): Promise<number> {
    if (!events || events.length === 0) return 0

    const db = await this.ensureDB()
    const now = Date.now()

    return new Promise<number>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE, META_STORE], 'readwrite')
      const store = transaction.objectStore(EVENTS_STORE)

      let count = 0

      events.forEach(event => {
        // 添加緩存時間戳
        const eventWithMeta: CachedEvent = {
          ...event,
          _cachedAt: now
        }

        const request = store.put(eventWithMeta)
        request.onsuccess = () => count++
        request.onerror = () => console.warn('[EventService] 存儲事件失敗:', request.error)
      })

      transaction.oncomplete = async () => {
        // 更新元數據
        await this._updateMetaAfterSave(events)
        console.log(`[EventService] 已存儲 ${count} 個事件`)
        resolve(count)
      }

      transaction.onerror = () => {
        console.error('[EventService] 批量存儲失敗:', transaction.error)
        reject(transaction.error)
      }
    })
  }

  /**
   * 更新存儲後的元數據
   */
  private async _updateMetaAfterSave(events: EventRow[]): Promise<void> {
    if (!events || events.length === 0) return

    const meta = await this.getMeta()
    const now = Date.now()

    // 計算最早和最晚的事件日期
    let oldestDate = meta.oldestEventDate || now
    let newestDate = meta.newestEventDate || 0

    events.forEach(e => {
      const created = new Date(e.created_at).getTime()
      if (created < oldestDate) oldestDate = created
      if (created > newestDate) newestDate = created
    })

    // 獲取當前事件總數
    const eventCount = await this._getEventCount()

    await this.updateMeta({
      key: 'sync',
      lastSyncTime: now,
      oldestEventDate: oldestDate,
      newestEventDate: newestDate,
      eventCount
    })
  }

  /**
   * 獲取事件總數
   */
  private async _getEventCount(): Promise<number> {
    const db = await this.ensureDB()

    return new Promise<number>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readonly')
      const store = transaction.objectStore(EVENTS_STORE)
      const request = store.count()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 檢查緩存是否有資料
   */
  async hasData(): Promise<boolean> {
    try {
      const count = await this._getEventCount()
      return count > 0
    } catch (error) {
      console.warn('[EventService] 檢查緩存資料失敗:', error)
      return false
    }
  }

  /**
   * 根據 ID 獲取單一事件
   * @param id - 事件 ID
   */
  async getEventById(id: string): Promise<CachedEvent | null> {
    const db = await this.ensureDB()

    return new Promise<CachedEvent | null>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readonly')
      const store = transaction.objectStore(EVENTS_STORE)
      const request = store.get(id)

      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 更新單一事件的部分欄位
   * @param id - 事件 ID
   * @param changes - 要更新的欄位
   * @returns 更新後的事件
   */
  async updateEvent(id: string, changes: Partial<EventRow>): Promise<CachedEvent | null> {
    const db = await this.ensureDB()
    const existing = await this.getEventById(id)

    if (!existing) {
      console.warn(`[EventService] 找不到事件 ${id}，無法更新`)
      return null
    }

    const updated: CachedEvent = {
      ...existing,
      ...changes,
      _cachedAt: Date.now()
    }

    return new Promise<CachedEvent>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readwrite')
      const store = transaction.objectStore(EVENTS_STORE)
      const request = store.put(updated)

      request.onsuccess = () => resolve(updated)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 刪除單一事件
   * @param id - 事件 ID
   */
  async deleteEvent(id: string): Promise<boolean> {
    const db = await this.ensureDB()

    return new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readwrite')
      const store = transaction.objectStore(EVENTS_STORE)
      const request = store.delete(id)

      request.onsuccess = () => resolve(true)
      request.onerror = () => reject(request.error)
    })
  }

  // ==================== 查詢操作 ====================

  /**
   * 查詢事件（支援 EventQueryBuilder 的 filters 格式）
   * @param filters - 篩選條件
   * @param options - 查詢選項 { orderBy, limit, offset }
   */
  async queryEvents(
    filters: CacheQueryFilters = {},
    options: CacheQueryOptions = {}
  ): Promise<CachedEvent[]> {
    const db = await this.ensureDB()

    return new Promise<CachedEvent[]>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readonly')
      const store = transaction.objectStore(EVENTS_STORE)

      // 獲取所有事件，然後在記憶體中過濾
      // 注意：對於大數據集，可能需要優化使用索引
      const request = store.getAll()

      request.onsuccess = () => {
        let results: CachedEvent[] = request.result || []

        // 應用篩選
        results = this._applyFilters(results, filters)

        // 應用排序
        if (options.orderBy && options.orderBy.length > 0) {
          results = this._applySort(results, options.orderBy)
        } else {
          // 預設按 created_at 降序
          results.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        }

        // 應用分頁
        if (options.offset !== undefined || options.limit !== undefined) {
          const start = options.offset || 0
          const end = options.limit ? start + options.limit : undefined
          results = results.slice(start, end)
        }

        resolve(results)
      }

      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 應用篩選條件
   */
  private _applyFilters(events: CachedEvent[], filters: CacheQueryFilters): CachedEvent[] {
    const f = filters

    return events.filter(event => {
      // 過濾掉已軟刪除的事件（除非明確要求查詢已刪除的）
      if (!f.includeDeleted && event.deleted_at) return false

      // ID 篩選
      if (f.id && event.id !== f.id) return false

      // 類型篩選
      if (f.type && event.type !== f.type) return false
      if (f.types && f.types.length > 0 && !f.types.includes(event.type)) return false

      // 優先級篩選
      if (f.priority && event.priority !== f.priority) return false

      // 來源篩選
      if (f.source && event.source !== f.source) return false
      if (f.source_detail && event.source_detail !== f.source_detail) return false

      // 層級篩選
      if (f.event_level && event.event_level !== f.event_level) return false

      // 組織篩選
      if (f.organization_id && event.organization_id !== f.organization_id) return false

      // 案場篩選
      if (f.field_id && event.field_id !== f.field_id) return false
      if (f.field_ids && f.field_ids.length > 0) {
        // 組織層級和系統層級的事件不進行 field_id 篩選
        if (event.event_level !== 'organization' && event.event_level !== 'system') {
          if (!event.field_id || !f.field_ids.includes(event.field_id)) return false
        }
      }

      // 設備篩選
      if (f.device_id && event.device_id !== f.device_id) return false

      // 日期篩選
      if (f.startDate) {
        const eventDate = new Date(event.created_at)
        const startDate = new Date(f.startDate)
        if (eventDate < startDate) return false
      }
      if (f.endDate) {
        const eventDate = new Date(event.created_at)
        const endDate = new Date(f.endDate)
        endDate.setHours(23, 59, 59, 999)
        if (eventDate > endDate) return false
      }
      if (f.createdAfter) {
        const eventDate = new Date(event.created_at)
        const afterDate = new Date(f.createdAfter)
        if (eventDate <= afterDate) return false
      }

      // 狀態篩選
      if (f.is_read !== undefined && event.is_read !== f.is_read) return false
      if (f.is_resolved !== undefined && event.is_resolved !== f.is_resolved) return false

      // 關鍵字搜尋
      if (f.keyword) {
        const keyword = f.keyword.toLowerCase()
        const searchFields = [
          event.title,
          event.content,
          event.field_name,
          event.device_name
        ].filter((s): s is string => Boolean(s)).map(s => s.toLowerCase())

        if (!searchFields.some(s => s.includes(keyword))) return false
      }

      // 最小持續時間篩選
      // duration_minutes: 已解決事件為實際分鐘數，未解決事件為 9999
      if (f.min_duration_minutes && f.min_duration_minutes > 0) {
        const minMinutes = f.min_duration_minutes

        if (event.is_resolved) {
          // 已解決事件：使用 duration_minutes 欄位
          if ((event.duration_minutes ?? 9999) < minMinutes) return false
        } else if (event.created_at) {
          // 未解決事件：動態計算從創建到現在的時間
          const durationMs = Date.now() - new Date(event.created_at).getTime()
          if (durationMs < minMinutes * 60 * 1000) return false
        }
      }

      return true
    })
  }

  /**
   * 應用排序
   */
  private _applySort(events: CachedEvent[], orderBy: OrderByOption[]): CachedEvent[] {
    return events.sort((a, b) => {
      for (const order of orderBy) {
        const aVal = a[order.field as keyof CachedEvent]
        const bVal = b[order.field as keyof CachedEvent]

        if (aVal === bVal) continue

        // 處理日期比較
        if (order.field.includes('_at') || order.field.includes('date')) {
          const aDate = new Date(aVal as string || 0).getTime()
          const bDate = new Date(bVal as string || 0).getTime()
          const comparison = aDate - bDate
          return order.ascending ? comparison : -comparison
        }

        // 一般比較
        const comparison = (aVal as unknown) < (bVal as unknown) ? -1 : 1
        return order.ascending ? comparison : -comparison
      }
      return 0
    })
  }

  /**
   * 計數查詢
   * @param filters - 篩選條件
   */
  async count(filters: CacheQueryFilters = {}): Promise<number> {
    const results = await this.queryEvents(filters, {})
    return results.length
  }

  // ==================== 元數據管理 ====================

  /**
   * 獲取緩存元數據
   * @returns { lastSyncTime, oldestEventDate, newestEventDate, eventCount }
   */
  async getMeta(): Promise<CacheMeta> {
    const db = await this.ensureDB()

    return new Promise<CacheMeta>((resolve, reject) => {
      const transaction = db.transaction([META_STORE], 'readonly')
      const store = transaction.objectStore(META_STORE)
      const request = store.get('sync')

      request.onsuccess = () => {
        const result: CacheMeta = request.result || {
          key: 'sync',
          lastSyncTime: null,
          oldestEventDate: null,
          newestEventDate: null,
          eventCount: 0
        }
        resolve(result)
      }

      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 更新緩存元數據
   * @param meta - 元數據
   */
  async updateMeta(meta: CacheMeta): Promise<boolean> {
    const db = await this.ensureDB()

    return new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([META_STORE], 'readwrite')
      const store = transaction.objectStore(META_STORE)
      const request = store.put({ key: 'sync', ...meta })

      request.onsuccess = () => resolve(true)
      request.onerror = () => reject(request.error)
    })
  }

  // ==================== 清理操作 ====================

  /**
   * 清理過期事件
   * @param maxDays - 最大保留天數（預設 30）
   * @returns 清理的事件數量
   */
  async clearExpired(maxDays: number = 30): Promise<number> {
    const db = await this.ensureDB()
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - maxDays)

    return new Promise<number>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readwrite')
      const store = transaction.objectStore(EVENTS_STORE)
      const index = store.index('created_at')

      // 使用游標遍歷過期事件
      const range = IDBKeyRange.upperBound(cutoffDate.toISOString())
      const request = index.openCursor(range)

      let count = 0

      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          store.delete(cursor.primaryKey)
          count++
          cursor.continue()
        }
      }

      transaction.oncomplete = async () => {
        if (count > 0) {
          // 更新元數據
          const eventCount = await this._getEventCount()
          const meta = await this.getMeta()
          await this.updateMeta({ ...meta, eventCount })

          console.log(`[EventService] 已清理 ${count} 個過期事件`)
        }
        resolve(count)
      }

      transaction.onerror = () => reject(transaction.error)
    })
  }

  /**
   * 完全清除所有緩存
   */
  async clearAll(): Promise<boolean> {
    const db = await this.ensureDB()

    return new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE, META_STORE], 'readwrite')

      const eventsStore = transaction.objectStore(EVENTS_STORE)
      eventsStore.clear()

      const metaStore = transaction.objectStore(META_STORE)
      metaStore.clear()

      transaction.oncomplete = () => {
        console.log('[EventService] 已清除所有緩存')
        resolve(true)
      }

      transaction.onerror = () => reject(transaction.error)
    })
  }

  /**
   * 關閉資料庫連接
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.isInitialized = false
      this.initPromise = null
      console.log('[EventService] 資料庫已關閉')
    }
  }
}

// 建立單例
const eventCacheService = new EventCacheService()
export { eventCacheService }
export default eventCacheService
